import { AdminLayout } from "./AdminLayout";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Copy, Send, Plus, ExternalLink, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

const MockupRequestDetail = () => {
  const { id } = useParams();
  const [mockup, setMockup] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [buildForm, setBuildForm] = useState({ preview_url: '', notes_client: '', notes_internal: '' });

  useEffect(() => {
    fetchMockup();
  }, [id]);

  const fetchMockup = async () => {
    try {
      const response = await axios.get(`${API}/client-mockups/mockups/${id}`, { withCredentials: true });
      setMockup(response.data);
    } catch (error) {
      toast.error('Failed to fetch mockup');
    }
  };

  const generatePromptPack = async () => {
    try {
      const response = await axios.post(`${API}/client-mockups/mockups/${id}/generate-prompt-pack`, {}, { withCredentials: true });
      toast.success(`Prompt pack v${response.data.version} generated`);
      fetchMockup();
    } catch (error) {
      toast.error('Failed to generate prompt pack');
    }
  };

  const copyPromptPack = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Prompt pack copied!');
  };

  const createBuild = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/client-mockups/mockups/${id}/builds`, buildForm, { withCredentials: true });
      toast.success('Build created');
      setIsDialogOpen(false);
      setBuildForm({ preview_url: '', notes_client: '', notes_internal: '' });
      fetchMockup();
    } catch (error) {
      toast.error('Failed to create build');
    }
  };

  const createShareLink = async (buildId) => {
    try {
      const response = await axios.post(`${API}/client-mockups/builds/${buildId}/share-links`, {}, { withCredentials: true });
      const url = response.data.full_url || response.data.share_url;
      navigator.clipboard.writeText(url);
      toast.success('Share link created and copied!');
      fetchMockup();
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  const sendEmail = async (buildId) => {
    try {
      await axios.post(`${API}/client-mockups/builds/${buildId}/send-email`, {}, { withCredentials: true });
      toast.success('Email sent successfully!');
      fetchMockup();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send email';
      toast.error(msg);
    }
  };

  if (!mockup) return <AdminLayout title="Mockup"><div className="text-center py-12">Loading...</div></AdminLayout>;

  const latestPromptPack = mockup.prompt_packs?.[mockup.prompt_packs.length - 1];

  return (
    <AdminLayout title="Mockup Request">
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold">{mockup.title}</h2>
          <p className="text-sm text-[#8A8A8A]">Goal: {mockup.goal}</p>
        </div>

        <div className="card-dashboard">
          <h3 className="text-base font-bold mb-3">Brief Summary</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-[#8A8A8A]">Client:</span> {mockup.lead?.company_name}</div>
            <div><span className="text-[#8A8A8A]">Status:</span> <span className="badge bg-blue-500/20 text-blue-400 ml-2">{mockup.status}</span></div>
            <div><span className="text-[#8A8A8A]">Pages:</span> {mockup.pages?.join(', ')}</div>
            <div><span className="text-[#8A8A8A]">Must Haves:</span> {mockup.must_haves?.join(', ') || 'None'}</div>
          </div>
        </div>

        <div className="card-dashboard">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-bold">Prompt Pack</h3>
            <button onClick={generatePromptPack} className="btn-secondary text-sm flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              {latestPromptPack ? `Regenerate v${latestPromptPack.version + 1}` : 'Generate v1'}
            </button>
          </div>
          {latestPromptPack ? (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#8A8A8A]">Version {latestPromptPack.version}</span>
                <button onClick={() => copyPromptPack(latestPromptPack.content_markdown)} className="btn-secondary text-sm flex items-center gap-2">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <div className="bg-[#0E0E0E] p-4 rounded border border-[#2E2E2E] max-h-96 overflow-y-auto">
                <pre className="text-xs text-[#8A8A8A] whitespace-pre-wrap">{latestPromptPack.content_markdown}</pre>
              </div>
            </div>
          ) : (
            <p className="text-[#8A8A8A] text-sm">No prompt pack generated yet</p>
          )}
        </div>

        <div className="card-dashboard">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-bold">Builds</h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <button className="btn-primary text-sm flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Add Build
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                <DialogHeader><DialogTitle>Add Build</DialogTitle></DialogHeader>
                <form onSubmit={createBuild} className="space-y-4 mt-4">
                  <input type="url" placeholder="Preview URL *" required value={buildForm.preview_url}
                    onChange={(e) => setBuildForm({...buildForm, preview_url: e.target.value})} className="input-field" />
                  <textarea rows={3} placeholder="Client Notes (shown on share page)" value={buildForm.notes_client}
                    onChange={(e) => setBuildForm({...buildForm, notes_client: e.target.value})} className="input-field resize-none" />
                  <textarea rows={2} placeholder="Internal Notes" value={buildForm.notes_internal}
                    onChange={(e) => setBuildForm({...buildForm, notes_internal: e.target.value})} className="input-field resize-none" />
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                    <button type="submit" className="btn-primary text-sm">Create Build</button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            {mockup.mockup_builds?.map((build) => (
              <div key={build.id} className="border-t border-[#2E2E2E] pt-4 mt-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-sm font-medium">Version {build.version}</span>
                    <div className="text-sm text-[#8A8A8A] mt-1">{new Date(build.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => createShareLink(build.id)} className="btn-secondary text-sm flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" /> Create Share Link
                    </button>
                    <button onClick={() => sendEmail(build.id)} className="btn-primary text-sm flex items-center gap-2">
                      <Send className="w-4 h-4" /> Send Email
                    </button>
                  </div>
                </div>
                <div className="text-sm">
                  <a href={build.preview_url} target="_blank" rel="noopener noreferrer" className="text-[#5F8FB8] hover:underline">
                    {build.preview_url}
                  </a>
                </div>
                {build.share_links?.map((link) => (
                  <div key={link.id} className="mt-2 text-xs text-[#8A8A8A]">
                    Share: /share/mockup/{link.token.substring(0, 12)}...
                  </div>
                ))}
              </div>
            )) || <p className="text-[#8A8A8A] text-sm">No builds yet</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MockupRequestDetail;
